import type { OnChangeValue, Option } from '@ksyos/design-system';
import {
    Button,
    Divider,
    Form,
    FormGroup,
    FormParagraph,
    Modal,
    Select,
    TextArea,
    TextInput,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IDeviceStatus } from '@common/interfaces/deviceStatus';
import type { IDeviceStatusWithOrganizations } from '@common/interfaces/deviceStatusWithOrganizations';
import type { IDeviceType, IDeviceTypeAddedServices } from '@common/interfaces/deviceType';
import { ManagerAuthorization, PractitionerRoleInRequest } from '@common/enums';
import { DeviceStatusReferenceType } from '@common/enums/deviceStatusReferenceType';
import { removeDuplicatedObjectsByKey } from '@common/utils/removeDuplicatedObjectsByKey';
import { KsyosPractitionersDropdown } from '@frontend/components/elements/KsyosPractitionersDropdown';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization, getValidationErrorTranslation } from '@frontend/utils';
import { enumToDropdownIOptions } from '@frontend/utils/enumToDropdownIOptions';
import type { ValidationErrors } from '../../../utils/validation';
import type { IOrganization } from '../../interfaces/organization';
import { DeviceStatus } from '../../../../common/enums/deviceStatus';
import { ValidationErrorCode } from '../../../../common/enums/validationErrorCode';
import { deviceAddSchema } from '../../../../common/validation/device';
import { fetchJson } from '../../../store/fetch';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../utils/validation';
import { DeviceStatusOrganizationsSelection } from './DeviceStatusOrganizationsSelection';

interface IDevice {
    deviceStatusWithOrganizations: IDeviceStatusWithOrganizations;
    deviceTypeId: string | null;
    externalId: string | null;
    notes: string | null;
}

interface IAddDeviceModalProps {
    onClose: (isSuccessful: boolean) => void;
    title: string;
}

const emptyDevice: IDevice = {
    deviceTypeId: null,
    externalId: null,
    notes: null,
    deviceStatusWithOrganizations: {
        deviceStatus: {
            reference: null,
            referenceType: null,
            status: DeviceStatus.inStock,
        },
        organizationIds: [],
    },
};

export function AddDeviceModal(props: IAddDeviceModalProps) {
    const statusList: Option[] = enumToDropdownIOptions(DeviceStatus, 'deviceStatusEnum');
    const [isSaveModalButtonEnabled, setIsSaveModalButtonEnabled] = useState<boolean>(false);
    const [deviceTypesList, setDeviceTypesList] = useState<IDeviceType[]>([]);
    const [deviceTypeOptionsList, setDeviceTypeOptionsList] = useState<Option[]>([]);
    const [organizationOptionsList, setOrganizationOptionsList] = useState<Option[]>([]);
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [device, setDevice] = useState<IDevice>(emptyDevice);
    const [deviceStatusOrganizationsError, setDeviceStatusOrganizationsError] = useState<string | null>();

    assertHasAuthorization(ManagerAuthorization.devicesAddEdit);

    useEffect(() => {
        if (!deviceTypesList.length) {
            loadDeviceTypes();
        }
    }, []);

    useEffect(() => {
        if (device.deviceTypeId) {
            initializeIssuedStatusFields();
            setServiceIdsForSelectedDeviceType(device.deviceTypeId);
        }
    }, [device.deviceTypeId]);

    useEffect(() => {
        initializeIssuedStatusFields();
    }, [device.deviceStatusWithOrganizations.deviceStatus.status]);

    useEffect(() => {
        validateForm();
    }, [device, deviceStatusOrganizationsError]);

    return (
        <Modal
            isOpen
            onClose={() => props.onClose(false)}
            size="medium"
        >
            <Modal.Header>{props.title}</Modal.Header>
            <Modal.CloseButton />
            <Modal.Body>
                <Form submitOnEnter={false}>
                    <FormGroup
                        hasErrors={validationErrors.has('deviceTypeId')}
                        isRequired={true}
                        label={translateKey('devices.type')}
                    >
                        {deviceTypeOptionsList.length && (
                            <Select
                                className="automation-add-device-modal-select-device-type"
                                error={getValidationErrorTranslation(validationErrors.get('deviceTypeId'))}
                                onChange={(option: OnChangeValue<Option, false>) =>
                                    handleChange('deviceTypeId', option!.value)
                                }
                                options={deviceTypeOptionsList}
                                placeholder={translateKey('devices.deviceTypePlaceholder')}
                                value={device.deviceTypeId ? getSelectedDeviceTypeOption(device.deviceTypeId) : null}
                            />
                        )}
                    </FormGroup>

                    <FormGroup
                        hasErrors={validationErrors.has('externalId')}
                        isRequired={true}
                        label={translateKey('devices.serialNumber')}
                    >
                        <TextInput
                            className="automation-add-device-modal-text-input-serial-number"
                            error={getValidationErrorTranslation(validationErrors.get('externalId'))}
                            onBlur={() => validateOne('externalId')}
                            onChange={(e) => handleChange('externalId', e.target.value)}
                            placeholder={translateKey('devices.serialNumberPlaceholder')}
                            value={device.externalId || ''}
                            variant="outline"
                        />
                    </FormGroup>

                    <FormGroup
                        hasErrors={validationErrors.has('notes')}
                        label={translateKey('devices.notes')}
                    >
                        <TextArea
                            className="automation-add-device-modal-text-input-device-notes"
                            error={getValidationErrorTranslation(validationErrors.get('notes'))}
                            onBlur={() => validateOne('notes')}
                            onChange={(e) => handleChange('notes', e.target.value || null)}
                            placeholder={`${translateKey('devices.notes')}...`}
                            value={device.notes || ''}
                            variant="outline"
                        />
                    </FormGroup>

                    <Divider
                        color="minimal"
                        spacing="medium"
                    />
                    <FormGroup
                        hasErrors={validationErrors.has('status')}
                        isRequired={true}
                        label={translateKey('common.status')}
                    >
                        <Select
                            className="automation-add-device-modal-select-status"
                            defaultValue={statusList.find(
                                (item: Option) =>
                                    item.value === device.deviceStatusWithOrganizations.deviceStatus.status
                            )}
                            error={getValidationErrorTranslation(validationErrors.get('status'))}
                            name={'deviceStatus'}
                            onChange={(option: any) => handleChange('status', option!.value)}
                            options={statusList}
                        />
                    </FormGroup>

                    {renderDeviceStatusExtraFields(device.deviceStatusWithOrganizations.deviceStatus.status)}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    className="automation-add-device-modal-button-cancel"
                    onClick={() => props.onClose(false)}
                    variant="outline"
                >
                    {translateKey('common.cancel')}
                </Button>
                <Button
                    className="automation-add-device-modal-button-save"
                    isDisabled={!isSaveModalButtonEnabled}
                    onClick={async () => await saveDevice()}
                    variant="solid"
                >
                    {translateKey('common.save')}
                </Button>
            </Modal.Footer>
        </Modal>
    );

    function renderDeviceStatusExtraFields(status: DeviceStatus) {
        switch (status) {
            case DeviceStatus.withPatient:
                return (
                    <FormGroup
                        hasErrors={validationErrors.has('reference')}
                        isRequired={true}
                        label={translateKey('devices.processId')}
                    >
                        <TextInput
                            className="automation-add-device-modal-text-input-processid"
                            error={getValidationErrorTranslation(validationErrors.get('reference'))}
                            onChange={(e) => {
                                handleChangeDeviceStatusReference(DeviceStatusReferenceType.processId, e.target.value);
                            }}
                            placeholder={translateKey('devices.processId')}
                            value={device.deviceStatusWithOrganizations.deviceStatus.reference || ''}
                            variant="outline"
                        />
                    </FormGroup>
                );
            case DeviceStatus.homeExamination:
                return (
                    device.deviceTypeId && (
                        <FormGroup label={translateKey('devices.partner')}>
                            <FormParagraph>{getManufacturerByDeviceTypeId(device.deviceTypeId)}</FormParagraph>
                        </FormGroup>
                    )
                );
            case DeviceStatus.sentForRepair:
                return (
                    device.deviceTypeId && (
                        <FormGroup label={translateKey('devices.forRepair')}>
                            <FormParagraph>{getManufacturerByDeviceTypeId(device.deviceTypeId)}</FormParagraph>
                        </FormGroup>
                    )
                );
            case DeviceStatus.issued:
                if (device.deviceTypeId) {
                    return (
                        <DeviceStatusOrganizationsSelection
                            className="automation-add-device-modal-select-device-status-organisation"
                            onChange={(options: string[]) => handleChangeOrganizationIds(options)}
                            onChangeValidationError={(error?: string) =>
                                setDeviceStatusOrganizationsError(error ?? null)
                            }
                            organizationOptionsList={organizationOptionsList}
                            selectedDeviceStatusOrganizationIds={device.deviceStatusWithOrganizations.organizationIds}
                        />
                    );
                } else {
                    return <></>;
                }

            case DeviceStatus.internalUse:
                return (
                    <FormGroup
                        data-automation-test="ksyosPractitioner"
                        hasErrors={validationErrors.has('deviceStatus.reference')}
                        isRequired={true}
                        label={translateKey('devices.assistant')}
                    >
                        <KsyosPractitionersDropdown
                            className="automation-add-device-modal-select-device-status-practitioner"
                            currentPractitionerId={device.deviceStatusWithOrganizations.deviceStatus.reference}
                            errorKey={validationErrors?.get('deviceStatus.reference')}
                            onChange={(option) => {
                                handleChangeDeviceStatusReference(
                                    DeviceStatusReferenceType.practitionerId,
                                    option?.value ?? null
                                );
                            }}
                        />
                    </FormGroup>
                );

            default:
                return <></>;
        }
    }

    async function saveDevice() {
        const url = '/api/devices';
        const { errors, item } = validate(deviceAddSchema, device);

        setValidationErrors(errors);

        if (errors.size > 0) {
            return;
        }

        const result = await fetchJson(url, 'POST', item, [ValidationErrorCode.EXTERNALID_EXISTS]);

        if (result.errorCode === ValidationErrorCode.EXTERNALID_EXISTS) {
            setValidationErrors(emptyValidationErrors.set('externalId', ValidationErrorCode.EXTERNALID_EXISTS));

            return;
        }

        props.onClose(true);
    }

    function handleChange<K extends keyof IDevice, D extends keyof IDeviceStatus>(
        name: K | D,
        value: IDevice[K] | IDeviceStatus[D]
    ) {
        if (name === 'status') {
            setDevice({
                ...device,
                deviceStatusWithOrganizations: {
                    ...device.deviceStatusWithOrganizations,
                    deviceStatus: {
                        status: value,
                        reference: null,
                        referenceType: null,
                    } as IDeviceStatus,
                },
            });
        } else {
            setDevice({ ...device, [name]: value });
        }
    }

    function handleChangeDeviceStatusReference(name: DeviceStatusReferenceType, value: string | null) {
        setDevice({
            ...device,
            deviceStatusWithOrganizations: {
                ...device.deviceStatusWithOrganizations,
                deviceStatus: {
                    ...device.deviceStatusWithOrganizations.deviceStatus,
                    referenceType: name,
                    reference: value,
                },
            },
        });
    }

    function handleChangeOrganizationIds(organizationIds: string[]) {
        if (organizationIds.toString() !== device.deviceStatusWithOrganizations.organizationIds.toString()) {
            setDevice({
                ...device,
                deviceStatusWithOrganizations: {
                    ...device.deviceStatusWithOrganizations,
                    organizationIds: organizationIds,
                },
            });
        }
    }

    function validateForm() {
        const { errors } = validate(deviceAddSchema, device);
        const hasErrors = errors.size > 0 || deviceStatusOrganizationsError;
        setIsSaveModalButtonEnabled(!hasErrors);
    }

    function validateOne(name: keyof IDevice) {
        setValidationErrors(updateValidationErrorsForOne(deviceAddSchema, device, validationErrors, name));
    }

    async function loadDeviceTypes() {
        const result = await fetchJson('/api/deviceTypes', 'GET');

        const deviceTypeOptions = result.json.items.map((item: IDeviceType) => ({
            value: item.id,
            label: item.name,
        }));

        setDeviceTypesList(result.json.items);
        setDeviceTypeOptionsList(deviceTypeOptions);
    }

    async function loadOrganizations(servicesForSelectedDeviceType: IDeviceTypeAddedServices) {
        const providingServiceIds = servicesForSelectedDeviceType.providingServices
            .map((providingService) => providingService.id)
            .join(',');
        const consumingServiceIds = servicesForSelectedDeviceType.consumingServices
            .map((consumingService) => consumingService.id)
            .join(',');

        let consumingServicesOrganizations: Array<IOrganization> = [];
        let providingServicesOrganizations: Array<IOrganization> = [];
        if (consumingServiceIds.length) {
            const result = await fetchJson(
                `/api/serviceConsumersList/${consumingServiceIds}/organizations?&isDeviceOrganizationSelection=true`,
                'GET'
            );
            consumingServicesOrganizations = result.json.items;
        }
        if (providingServiceIds.length) {
            const result = await fetchJson(
                `/api/serviceProvidersList/${providingServiceIds}/organizations?role=${PractitionerRoleInRequest.biometrician}`,
                'GET'
            );
            providingServicesOrganizations = result.json.items;
        }

        const serviceOrganizationsWithDuplicates = [
            ...consumingServicesOrganizations,
            ...providingServicesOrganizations,
        ];
        const serviceOrganizations = removeDuplicatedObjectsByKey(serviceOrganizationsWithDuplicates, 'id');

        setOrganizationOptionsList(
            serviceOrganizations.map((item) => ({
                value: item.id,
                label: `${item.name} ${item.txcsId}`,
            }))
        );
    }

    async function setServiceIdsForSelectedDeviceType(deviceTypeId: string) {
        const index = deviceTypesList.findIndex((deviceType) => deviceType.id === deviceTypeId);
        const servicesForSelectedDeviceType = {
            providingServices: deviceTypesList[index].providingServices,
            consumingServices: deviceTypesList[index].consumingServices,
        };
        await loadOrganizations(servicesForSelectedDeviceType);
    }

    function initializeIssuedStatusFields() {
        setDeviceStatusOrganizationsError(null);
        setDevice({
            ...device,
            deviceStatusWithOrganizations: {
                ...device.deviceStatusWithOrganizations,
                organizationIds: [],
            },
        });
    }

    function getSelectedDeviceTypeOption(deviceTypeId: string) {
        return deviceTypeOptionsList.find((item: Option) => item.value === deviceTypeId);
    }

    function getManufacturerByDeviceTypeId(deviceTypeId: string) {
        return deviceTypesList.find((deviceType) => deviceType.id === deviceTypeId)?.manufacturer;
    }
}
